// src/components/FloatingDashboardCTA/FloatingDashboardCTA.jsx
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import NeonButton from "../../components/ResponsiveButton/NeonButton.jsx";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../services/api";

/**
 * Reittisäännöt (WHITELIST)
 * -----------------------------------------
 * pattern      : reittipattern (tukee :param ja *-wildcardin)
 * show         : näytä nappi tällä sivulla?
 * requireAuth  : vaatiiko kirjautumisen? (oletus true)
 * requireOwner : pitääkö käyttäjän omistaa/kytkeytyä kohteeseen (esim. bändi)?
 * ownerParam   : mistä path-paramista kohde luetaan (slug | id)
 *
 * VAIHDA vain `show: true/false` (tai kommentoi rivi).
 */
const ROUTE_RULES = [
  // Julkiset sivut
  { pattern: "/",                         show: false }, /* EI NÄYTETÄ */
  { pattern: "/blogi",                    show: false }, /* EI NÄYTETÄ */
  { pattern: "/blogi/:slug",              show: false }, /* EI NÄYTETÄ */
  { pattern: "/tietoameista",             show: false }, /* EI NÄYTETÄ */
  { pattern: "/useinkysyttya",            show: false }, /* EI NÄYTETÄ */
  { pattern: "/ukk-esiintyjille",         show: false }, /* EI NÄYTETÄ */
  { pattern: "/otayhteytta",              show: false }, /* EI NÄYTETÄ */
  { pattern: "/liity-esiintyjaksi",       show: false }, /* EI NÄYTETÄ */
  { pattern: "/ukk",                      show: false }, /* EI NÄYTETÄ (redirect) */
  { pattern: "/kayttoehdot",              show: false }, /* EI NÄYTETÄ */
  { pattern: "/tietosuoja",               show: false }, /* EI NÄYTETÄ */
  { pattern: "/varaus/kiitos",            show: false }, /* EI NÄYTETÄ */
  { pattern: "/varaus/peruttu",           show: false }, /* EI NÄYTETÄ */

  // Auth-flow
  { pattern: "/rekisteroidy",             show: false, requireAuth: false }, /* EI NÄYTETÄ */
  { pattern: "/kirjaudu",                 show: false, requireAuth: false }, /* EI NÄYTETÄ */
  { pattern: "/forgot",                   show: false, requireAuth: false }, /* EI NÄYTETÄ */
  { pattern: "/reset",                    show: false, requireAuth: false }, /* EI NÄYTETÄ */
  { pattern: "/verify-email",             show: false, requireAuth: false }, /* EI NÄYTETÄ */

  // Onboarding / setup
  { pattern: "/onboarding",               show: false }, /* EI NÄYTETÄ */
  { pattern: "/setup",                    show: false }, /* EI NÄYTETÄ */

  // Suojatut (hallinta)
  { pattern: "/dashboard",                show: false }, /* EI NÄYTETÄ */
  { pattern: "/backstage",                show: false }, /* EI NÄYTETÄ (esiintyjäsivu/hubi) */

  // Profiilisivut
  { pattern: "/esiintyja/:slug",          show: true,  requireOwner: true, ownerParam: "slug" }, /* NÄYTÄ esikatselussa */
  { pattern: "/preview/esiintyja/:slug",  show: true,  requireOwner: true, ownerParam: "slug" }, /* NÄYTÄ (vanhat linkit) */
  { pattern: "/yritys/:slug",             show: false, requireOwner: true, ownerParam: "slug" }, /* EI NÄYTETÄ nyt – vaihda true kun haluat */
];

/* ---------------- pieni pattern-matcher ---------------- */

// "/a/b/" -> ["a","b"]
function segs(path = "") {
  return String(path).replace(/(^\/+|\/+$)/g, "").split("/").filter(Boolean);
}

/** Matchaa pathname patterniin ja palauta { matched, params }. Tukee :param ja * */
function matchPattern(pattern, pathname) {
  const pSegs = segs(pattern);
  const aSegs = segs(pathname);
  const params = {};
  let i = 0, j = 0;

  while (i < pSegs.length && j < aSegs.length) {
    const p = pSegs[i];
    const a = aSegs[j];

    if (p === "*") return { matched: true, params }; // wildcard syö loput
    if (p.startsWith(":")) { params[p.slice(1)] = a; i++; j++; continue; }
    if (p === a) { i++; j++; continue; }
    return { matched: false, params: {} };
  }

  if (i < pSegs.length && pSegs[i] === "*") i++; // /blogi/* sallii ylijäämän

  const matched = i === pSegs.length && j === aSegs.length;
  return { matched, params: matched ? params : {} };
}

function findRuleForPath(pathname) {
  for (const rule of ROUTE_RULES) {
    const { matched, params } = matchPattern(rule.pattern, pathname);
    if (matched) return { ...rule, params };
  }
  return null;
}

export default function FloatingDashboardCTA() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // luetaan koko location, jotta päästään käsiksi state.backTo:hon
  const location = useLocation();
  const { pathname } = location;

    const isPreviewMode = useMemo(() => {
    const qs = new URLSearchParams(location.search || "");
    const path = location.pathname || "";
    return (
      path.startsWith("/preview/") ||
      location.state?.fromDashboard === true ||
      qs.has("preview") ||
      qs.has("preview_token") ||
      qs.has("preview") ||
      qs.has("preview_token") ||   // snake_case
      qs.has("previewToken") ||    // camelCase
      qs.get("mode") === "preview"
    );
  }, [location.pathname, location.search, location.state]);

  // Piilota CTA jos promolukko (tyhjä kalenteri) on aktiivinen
  const [promoLockActive, setPromoLockActive] = useState(false);
  useEffect(() => {
    const check = () =>
      setPromoLockActive(Boolean(document.querySelector('[data-promolock="true"]')));
    check();
    const mo = new MutationObserver(check);
    try { mo.observe(document.body, { subtree: true, attributes: true }); } catch {}
    return () => mo.disconnect();
  }, []);

  // HUOM: ei tehdä varhaista returnia ennen seuraavia hookeja
  const rule = useMemo(() => findRuleForPath(pathname), [pathname]);
  const [allowed, setAllowed] = useState(false);

  // Mikä kohde on URL:ssa (slug tai id) tämän sivun säännön mukaan
    const slugOrId = useMemo(() => {
      if (!rule) return "";
      const key = rule.ownerParam || "slug";
      return String(rule.params?.[key] || "").trim();
    }, [rule]);

  useEffect(() => {
    let cancelled = false;

    // Näytä nappi VAIN esikatselussa ja kun promolukko ei ole aktiivinen
    if (!isPreviewMode || promoLockActive) {
      setAllowed(false);
      return () => { cancelled = true; };
    }

    // Ei sääntöä tai show=false -> ei nappia
    if (!rule || !rule.show) {
      setAllowed(false);
      return () => { cancelled = true; };
    }

    // Auth-vaatimus (oletus true)
    const requireAuth = rule.requireAuth !== false;
    if (requireAuth && !user) {
      setAllowed(false);
      return () => { cancelled = true; };
    }

    // Ei omistajuusvaatimusta
    if (!rule.requireOwner) {
      setAllowed(true);
      return () => { cancelled = true; };
    }

  // Omistajuus/jäsenyys vaadittu -> tarkista käyttäjän bändit
  if (!slugOrId) {
      setAllowed(false);
      return () => { cancelled = true; };
    }

    (async () => {
      try {
        const res = await api.auth.meBands();
        const list = Array.isArray(res?.bands) ? res.bands : Array.isArray(res) ? res : [];

        const slugSet = new Set(
          list.map(m => String(m.band?.slug ?? m.slug ?? "").toLowerCase()).filter(Boolean)
        );
        const idSet = new Set(
          list.map(m => String(m.band?.id ?? m.band_id ?? m.id ?? "")).filter(Boolean)
        );

       let ok = false;

      if (/^\d+$/.test(slugOrId)) {
        ok = idSet.has(slugOrId);
       } else {
        ok = slugSet.has(slugOrId.toLowerCase());
         if (!ok) {
          const band = await api.bandByIdOrSlug(slugOrId).catch(() => null);
           const bid = String(band?.band?.id ?? band?.id ?? "");
           if (bid) ok = idSet.has(bid);
         }
       }

        if (!cancelled) setAllowed(ok);
      } catch {
        if (!cancelled) setAllowed(false);
      }
    })();

    return () => { cancelled = true; };
}, [rule, user, isPreviewMode, slugOrId, promoLockActive]);

  // Renderöi vain kun ehdot täyttyvät
  if (!allowed) return null;

  // --- Älykäs paluu ---
  const handleBackClick = () => {
    const backTo = location.state?.backTo;
    if (backTo?.pathname) {
      navigate({ pathname: backTo.pathname, search: backTo.search || "" });
      const y = typeof backTo.scrollY === "number" ? backTo.scrollY : 0;
      setTimeout(() => window.scrollTo(0, y), 0);
      return;
    }
    if (slugOrId) {
   navigate(`/dashboard?band=${encodeURIComponent(slugOrId)}&tab=basic`, { replace: true });
 } else {
   navigate(`/dashboard`, { replace: true });
 }
  };

  return (
    <div
      className="
        fixed left-1/2 -translate-x-1/2 z-40 pointer-events-none
        bottom-[88px] sm:bottom-24 md:bottom-10 mdd:bottom-12 lg:bottom-12 xl:bottom-14 2xl:bottom-16
      "
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <NeonButton
        size="sm"
        onClick={handleBackClick}
        className="pointer-events-auto rounded-full px-5 shadow-lg"
      >
        Lopeta esikatselu
      </NeonButton>
    </div>
  );
}
